import {
    AfterViewInit,
    ApplicationRef,
    ChangeDetectorRef,
    Component,
    ComponentRef,
    createComponent,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    Type,
    ViewChild,
    ViewContainerRef,
    inject,
} from '@angular/core'
import { RatingCellSettingsComponent } from '@app/feature/add-field/add-field-content/rating-cell-settings/rating-cell-settings.component'
import { StatusSelectCellSettingsComponent } from '@app/feature/add-field/add-field-content/select-settings/status-select-cell-settings/status-select-cell-settings.component'
import { LinkSettingsComponent } from '@app/shared/cell-types/link/link-settings/link-settings.component'
import { Field, FieldTypes } from '@core/models'
import { cloneDeep } from 'lodash-es'
import { LogService } from '@services/logger/log.service'
import {
    PeopleCellSettings,
    PeopleCellSettingsComponent,
} from '@app/feature/add-field/add-field-content/people-cell-settings/people-cell-settings.component'
import { DropdownSelectCellSettingsComponent } from '@app/feature/add-field/add-field-content/select-settings/dropdown-select-cell-settings/dropdown-select-cell-settings.component'
import {
    NumberCellSettings,
    NumberSettingsComponent,
} from '@app/feature/field-settings/number-settings/number-settings.component'
import { MoneySettingsComponent } from '@app/feature/field-settings/money-settings/money-settings.component'

export interface FieldSettingsComponent {
    field: Field
    configurationString?: string
    configurationChanged: EventEmitter<any>
}

export const NEW_SETTINGS_TYPES: string[] = [
    FieldTypes.STATUS,
    FieldTypes.DROPDOWN,
    FieldTypes.ASSIGNEE,
    FieldTypes.PEOPLE,
    FieldTypes.NUMBER,
    FieldTypes.RATING,
    FieldTypes.MONEY,
    FieldTypes.PERCENT,
]

export const fieldTypeColumnSettingsComponents: { [key: string]: Type<any> } = {
    [FieldTypes.STATUS]: StatusSelectCellSettingsComponent,
    [FieldTypes.DROPDOWN]: DropdownSelectCellSettingsComponent,
    [FieldTypes.RATING]: RatingCellSettingsComponent,
    [FieldTypes.MONEY]: MoneySettingsComponent,
    [FieldTypes.LINK]: LinkSettingsComponent,
    [FieldTypes.ASSIGNEE]: PeopleCellSettingsComponent,
    [FieldTypes.PEOPLE]: PeopleCellSettingsComponent,
    [FieldTypes.NUMBER]: NumberSettingsComponent,
    [FieldTypes.PERCENT]: NumberSettingsComponent,
}

export type FieldConfiguration = PeopleCellSettings | NumberCellSettings

@Component({
    selector: 'app-add-field-content',
    templateUrl: './add-field-content.component.html',
    styleUrls: ['./add-field-content.component.sass'],
    standalone: true,
})
export class AddFieldContentComponent implements OnInit, AfterViewInit, OnChanges {
    private readonly appRef = inject(ApplicationRef)
    private readonly cdr = inject(ChangeDetectorRef)
    private readonly logService = inject(LogService)

    @Input() field!: Field
    @Input() fieldType!: string

    @Output() fieldChanged: EventEmitter<Field> = new EventEmitter()
    @Output() configurationChanged: EventEmitter<FieldConfiguration> =
        new EventEmitter<FieldConfiguration>()
    @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>()

    @ViewChild('addColumnArea', { read: ViewContainerRef, static: true })
    addColumnArea!: ViewContainerRef

    currentComponent: ComponentRef<any> | null = null

    ngOnChanges(changes: SimpleChanges) {
        if (
            this.currentComponent &&
            !changes.field.isFirstChange() &&
            changes.field.previousValue?.default_value !== changes.field.currentValue.default_value
        ) {
            this.currentComponent.setInput('field', this.field)
        }
    }

    ngOnInit() {
        this.addFieldColumn(this.fieldType)
    }

    ngAfterViewInit() {
        this.cdr.detectChanges()
    }

    private addFieldColumn(fieldType: string) {
        this.addColumnArea.clear()
        const componentName = fieldTypeColumnSettingsComponents[fieldType]
        if (!componentName) {
            this.logService.info(
                'No custom settings for field with type ' + this.field.field_type_code,
            )
            return
        }

        this.currentComponent = createComponent(componentName, {
            environmentInjector: this.appRef.injector,
        })

        this.addColumnArea.insert(this.currentComponent.hostView)
        this.setComponentInstanceValues(fieldType)
    }

    private setComponentInstanceValues(fieldType: string) {
        if (fieldType === 'field_type_rating') {
            this.field = {
                ...this.field,
                configure_json: this.field.configure_json || JSON.stringify({ count: 5 }),
            }
        }

        if (!this.currentComponent) return

        this.currentComponent.setInput('field', this.field)
        this.currentComponent.instance.fieldChanged = this.fieldChanged
        this.currentComponent.instance.configurationChanged = this.configurationChanged
        this.currentComponent.instance.valid = this.valid

        if (fieldType === 'field_type_status') {
            this.currentComponent.setInput(
                'selectOptions',
                cloneDeep(this.field.select_object_field),
            )
        }

        const extendedSettingsTypes = [
            'field_type_number',
            'field_type_money',
            'field_type_persent',
        ]

        if (extendedSettingsTypes.includes(fieldType)) {
            this.currentComponent.setInput('extendedSettings', true)
        }

        this.cdr.detectChanges()
    }
}
